<template>
	<div v-if="isVisible" class="grid-flex-container">
		<v-container fluid class="pa-0">
			<v-toolbar :color="headerColor" flat dense>
				<v-btn @click="onCancel" elevation="1" fab x-small icon>
					<v-icon>mdi-arrow-left</v-icon>
				</v-btn>
				<v-toolbar-title v-if="hasRecordId"
					>Edit record</v-toolbar-title
				>
				<v-toolbar-title v-else>Create new record</v-toolbar-title>
			</v-toolbar>
		</v-container>
		<v-form
			ref="form"
			class="d-sm-block flex-column flex-wrap flex-grow-1"
			:class="getOverflowYClass"
		>
			<div
				:class="getFormFieldClass"
				tabindex="-1"
				v-for="(field, index) in formFields"
				:key="index"
			>
				<form-field
					:ref="getFormFieldRef(field)"
					v-if="field != null && record"
					:formField="field"
					:record="record"
					:tabIndex="getTabIndex(field)"
					:uniqueFields="uniqueFieldsToCheckIds"
					@removeFieldToCheckId="onRemoveFieldToCheckId"
					@pkFormFieldFocusChanged="onPkFormFieldFocusChanged"
				/>
			</div>
		</v-form>

		<v-container fluid class="align-end">
			<v-row no-gutters>
				<v-btn text outlined @click="onReset">
					<v-icon class="mr-1">mdi-refresh</v-icon>Reset
				</v-btn>
				<v-spacer />
				<v-btn text outlined class="mr-2" @click="onCancel"
					>Cancel</v-btn
				>
				<v-btn
					color="primary"
					@click="onSave"
					:disabled="pkFormFieldHasFocus"
					id="inventoryFormSaveBtn"
				>
					<v-icon class="mr-1">mdi-content-save</v-icon>Save
				</v-btn>
			</v-row>
		</v-container>
		<CheckLinkFieldsModal
			v-if="
				checkLinkFields_showDialog &&
				checkLinkFields_foreignKey &&
				checkLinkFields_gridView
			"
			@close="onCheckLinkFieldsModalClose"
			@submit="onCheckLinkFieldsModalSubmit"
			:dialog="checkLinkFields_showDialog"
			:gridView="checkLinkFields_gridView"
			:foreignKey="checkLinkFields_foreignKey"
		/>
		<CheckReferenceFieldsModal
			v-if="
				checkReferenceFields_showDialog &&
				checkReferenceField_referenceField
			"
			:dialog="checkReferenceFields_showDialog"
			:referenceField="checkReferenceField_referenceField"
			@close="onCheckReferenceFieldsModalClose"
			@submit="onCheckReferenceFieldsModalSubmit"
		/>
		<UniqueValuesDialog
			:showDialog="showDialog"
			:fields="uniqueFieldsToCheckLabels"
			@confirm="confirmUniqueFields"
		/>
	</div>
</template>

<script>
import CheckLinkFieldsModal from '../form/CheckLinkFieldsModal.vue';
import CheckReferenceFieldsModal from '../form/CheckReferenceFieldsModal.vue';
import FormField from '../form/FormField.vue';
import dataService from '../../../services/data/inventoryFormService';
import { InventoryFormFormActions } from '../../../store/actions';
import {
	InventoryFormFormGetters,
	AuthGetterTypes,
} from '../../../store/getters';
import { mapGetters } from 'vuex';
import { FieldDataTypes } from '../../../enums/fieldDataTypes';
import gridViewFieldsUtils from '../../../utils/gridViewFieldsUtils';
import { InventoryFormTypes } from '../../../enums/inventoryFormTypes';
import { processEnum } from '../../../enums/processEnum';
import checkLinkFieldsMixin from '../../../mixins/inventoryForm/checkLinkFieldsMixin';
import checkReferenceFieldsMixin from '../../../mixins/inventoryForm/checkReferenceFieldsMixin';
import { formFieldRule } from '../../../enums/formFieldRuleEnum';
import UniqueValuesDialog from './UniqueValuesDialog.vue';
import Snackbar from '../../core/dialogs/snackbarMessage';

export default {
	name: 'Form',
	components: {
		FormField,
		CheckLinkFieldsModal,
		CheckReferenceFieldsModal,
		UniqueValuesDialog,
	},
	mixins: [checkLinkFieldsMixin, checkReferenceFieldsMixin],
	data() {
		return {
			showDialog: false,
			uniqueFieldsToCheckLabels: [],
			uniqueFieldsToCheckIds: [],
			pkFormFieldHasFocus: false,
		};
	},

	computed: {
		getOverflowYClass() {
			if (this.columns) {
				if (this.columns == 1) return 'overflow-y-auto';
				if (!this.$vuetify.breakpoint.mdAndUp) return 'overflow-y-auto';
				return 'd-md-flex overflow-y-hidden';
			}
			return 'overflow-y-auto';
		},

		getFormFieldClass() {
			if (!this.$vuetify.breakpoint.mdAndUp) return 'formField-sm';
			if (!this.columns) return 'formField-md1';
			switch (this.columns) {
				case 1:
					return 'formField-md1';
				case 2:
					return 'formField-md2';
				case 3:
					return 'formField-md3';
				default:
					return 'formField-md1';
			}
		},
		...mapGetters({
			formFields: [InventoryFormFormGetters.FORM_FIELDS],
			hiddenFields: [InventoryFormFormGetters.HIDDEN_FORM_FIELDS],
			columns: [InventoryFormFormGetters.FORM_COLUMNS],
			defaultGridView: [InventoryFormFormGetters.DEFAULT_GRID_VIEW],
			record: [InventoryFormFormGetters.RECORD],
			selectedInventoryForm: [
				InventoryFormFormGetters.SELECTED_INVENTORY_FORM,
			],
			currentUserId: [AuthGetterTypes.CURRENT_USER_ID],
		}),
		isVisible() {
			if (!this.record) return false;
			if (!this.formFields) return false;
			if (!this.columns) return false;
			if (!this.defaultGridView) return false;
			return true;
		},

		formFieldsRefs() {
			if (!this.formFields) return [];
			return this.formFields.map((x) => 'formField_' + x.Id);
		},

		currentInventoryFormId() {
			return this.$route.params.formId;
		},
		recordId() {
			if (!this.record) return null;
			return this.record.Id;
		},
		headerColor() {
			return this.hasRecordId ? '#ffc00030' : '#ffffff';
		},
		hasRecordId() {
			if (this.record && this.record.Id) return true;
			return false;
		},
	},
	watch: {
		currentInventoryFormId: function () {
			this.loadInventoryForm();
		},
	},
	mounted() {
		this.loadInventoryForm().then(() => {
			this.$store.dispatch(InventoryFormFormActions.LOAD_RECORD, {
				record: {},
				userId: this.currentUserId,
			});
		});
	},
	destroyed() {
		this.$store.dispatch(InventoryFormFormActions.CLEAR);
	},
	methods: {
		onRemoveFieldToCheckId(id) {
			if (this.uniqueFieldsToCheckIds.length > 0) {
				let index = this.uniqueFieldsToCheckIds.findIndex(
					(e) => e === id
				);
				if (index >= 0) this.uniqueFieldsToCheckIds.splice(index, 1);
			}
		},
		checkUniqueValues() {
			let result = [];
			this.selectedInventoryForm.FormFields.forEach((x) => {
				let object = x.FormFieldRules.find(
					(x) => x.Operator == formFieldRule.MaxOne
				);

				if (object) {
					result.push(x);
				}
			});
			result = result.map((x) => ({
				EntityName: x.GridViewFields[0].DataField.EntityName,
				FieldName: x.GridViewFields[0].DataField.FieldName,
				FieldId: x.GridViewFields[0].DataField.Id,
				Value: this.record[
					gridViewFieldsUtils.getFieldName(x.GridViewFields[0])
				],
			}));
			return new Promise((resolve) => {
				dataService
					.checkUniqueValues(result)
					.then((resp) => resolve(resp.data));
			});
		},
		loadInventoryForm() {
			return this.$store.dispatch(
				InventoryFormFormActions.LOAD_INVENTORY_FORM,
				this.currentInventoryFormId
			);
		},
		getFormFieldRef(field) {
			let ref = 'formField_' + field.Id;
			return ref;
		},
		getTabIndex(field) {
			let offset = 1;
			if (this.formFields && this.formFields.length > 0) {
				let index = this.formFields.indexOf(field);
				if (index > 0) offset = offset + index;
			}
			return 100 + offset;
		},
		onReset() {
			this.$store
				.dispatch(InventoryFormFormActions.CLEAR_GRID_RECORD)
				.then(() => {
					this.$store
						.dispatch(InventoryFormFormActions.LOAD_RECORD, {
							record: {},
							userId: this.currentUserId,
						})
						.then(() => {
							this.$refs.form.resetValidation();
						});
				});
		},
		onCancel() {
			this.$store.dispatch(InventoryFormFormActions.CLEAR).then(() => {
				this.$router.push({
					name: 'InventoryGrid',
					params: { formId: this.currentInventoryFormId },
				});
			});
		},
		async onSave() {
			// this.$refs.form.resetValidation();
			this.uniqueFieldsToCheckLabels = [];
			this.uniqueFieldsToCheckIds = [];
			if (!this.validate()) {
				return;
			}

			this.handleHiddenFields();
			if (!this.validate()) {
				return;
			}

			let checkLinkFields = await this.checkLinkFields();
			if (!checkLinkFields) {
				return;
			}

			let checkReferenceFields = await this.checkReferenceFields();
			if (!checkReferenceFields) return;

			let checkUniqueValue = await this.checkUniqueValues();

			if (checkUniqueValue.ExistingConflicts === true) {
				let fields = [];
				this.selectedInventoryForm.FormFields.forEach((x) =>
					x.GridViewFields.forEach((gvf) => {
						if (
							checkUniqueValue.FieldNames.includes(
								gvf.DataFieldName
							)
						)
							fields.push(gvf.Label);
					})
				);
				this.uniqueFieldsToCheckLabels = fields;
				this.uniqueFieldsToCheckIds = checkUniqueValue.FieldIds;
				this.showDialog = true;
				this.validate();
				return;
			}
			this.$store
				.dispatch(
					InventoryFormFormActions.SAVE,
					processEnum.InventoryForm
				)
				.then((success) => {
					if (success) {
						switch (this.selectedInventoryForm.Type) {
							case InventoryFormTypes.Inventory:
								this.onReset();
								Snackbar({ message: 'Record saved!' });
								this.$refs.form.resetValidation();
								break;
							case InventoryFormTypes.RemoteUser:
								break;
							case InventoryFormTypes.SteadyState:
								Snackbar({ message: 'Record saved!' });
								this.$router.push(
									`/inventory/${this.currentInventoryFormId}/grid`
								);
								break;
						}
					}
				});
		},
		confirmUniqueFields() {
			this.showDialog = false;
		},
		handleHiddenFields() {
			this.hiddenFields.forEach((x) => {
				switch (x.GridViewFields[0].DataField.DataType) {
					case FieldDataTypes.DATETIME: {
						let key = gridViewFieldsUtils.getFieldName(
							x.GridViewFields[0]
						);
						let value = [
							{
								key: key,
								// value: new Date().toISOString().split("T")[0],
								value: new Date().toUTCString(),
							},
						];
						this.$store.dispatch(
							InventoryFormFormActions.UPDATE_RECORD_KEY,
							value
						);
						break;
					}
					case FieldDataTypes.USER: {
						let key = gridViewFieldsUtils.getFieldName(
							x.GridViewFields[0]
						);
						let value = [{ key: key, value: this.currentUserId }];
						this.$store.dispatch(
							InventoryFormFormActions.UPDATE_RECORD_KEY,
							value
						);
						break;
					}
				}
			});
		},
		validate() {
			return this.$refs['form'].validate();
		},
		onPkFormFieldFocusChanged(value) {
			this.pkFormFieldHasFocus = value;
		},
	},
};
</script>
